import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageSquareText, Radio, Star } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-color.png`;
const LOGO_WHITE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-white.png`;

const crr = (inn) => {
  const [o, b] = String(inn.overs || "0.0").split(".").map((v) => parseInt(v, 10) || 0);
  const balls = o * 6 + b;
  return balls > 0 ? (inn.runs / (balls / 6)).toFixed(2) : "0.00";
};

const hasScore = (inn) => inn && (inn.runs > 0 || inn.wickets > 0 || (inn.overs && inn.overs !== "0.0") || inn.batting);

const shortOf = (teams, name) => (teams.find((t) => t.name === name) || {}).short || name.split(" ").map((w) => w[0]).join("").slice(0, 3).toUpperCase();
const colourOf = (teams, name) => (teams.find((t) => t.name === name) || {}).colour || "#024ea7";

const InningsRow = ({ teams, inn, fallbackName, highlight }) => (
  <div className={`score-row ${highlight ? "batting" : ""}`}>
    <b><i className="score-chip" style={{ background: colourOf(teams, inn.batting || fallbackName) }} />{shortOf(teams, inn.batting || fallbackName)}</b>
    <span className="score-nums">{inn.runs}/{inn.wickets}<small>{inn.overs} ov · CRR {crr(inn)}</small></span>
  </div>
);

const BatTable = ({ inn, testid }) => (
  <div className="sc-table-wrap" data-testid={testid}>
    <table className="sc-table"><thead><tr><th>Batsman</th><th></th><th>R</th><th>B</th><th>4s</th><th>6s</th><th>SR</th></tr></thead>
      <tbody>{inn.batting.map((b, i) => <tr key={i} className={b.out === "not out" ? "notout" : ""}>
        <td>{b.name}</td><td className="sc-dismissal">{b.out}</td><td>{b.runs}</td><td>{b.balls}</td><td>{b.fours}</td><td>{b.sixes}</td>
        <td>{b.balls ? ((b.runs / b.balls) * 100).toFixed(1) : "0.0"}</td>
      </tr>)}</tbody></table>
  </div>
);

const BowlTable = ({ inn, testid }) => (
  <div className="sc-table-wrap" data-testid={testid}>
    <table className="sc-table"><thead><tr><th>Bowler</th><th>O</th><th>R</th><th>W</th><th>Econ</th><th>Wd</th><th>Nb</th></tr></thead>
      <tbody>{inn.bowling.map((b, i) => <tr key={i}>
        <td>{b.name}</td><td>{b.overs}</td><td>{b.runs}</td><td>{b.wickets}</td><td>{b.economy}</td><td>{b.wides}</td><td>{b.noballs}</td>
      </tr>)}</tbody></table>
  </div>
);

const InningsCard = ({ inn, label }) => (
  <div className="sc-innings">
    <p className="sc-inn-title">{inn.team} — {inn.runs}/{inn.wickets} ({inn.overs} ov)</p>
    <BatTable inn={inn} testid={`bat-table-${label}`} />
    <BowlTable inn={inn} testid={`bowl-table-${label}`} />
    {inn.fow.length > 0 && <p className="sc-fow" data-testid={`fow-${label}`}>Fall of wickets: {inn.fow.map((f) => `${f.score}/${f.wicket} (${f.player}, ${f.over})`).join(" · ")}</p>}
  </div>
);

const initials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const SquadTeam = ({ side, team }) => (
  <div className="sq-team">
    <p className="sq-team-name">{team}</p>
    {[["xi", "Playing XI"], ["impact", "Impact Players"], ["bench", "Bench"]].map(([group, label]) => side[group].length > 0 && <div key={group}>
      <p className="sq-group">{label}</p>
      <div className="sq-grid">{side[group].map((p) => <div className="sq-player" key={p.name} data-testid={`sq-player-${p.name.replace(/\s/g, "-")}`}>
        {p.photo ? <img src={`${BACKEND_URL}${p.photo}`} alt={p.name} loading="lazy" /> : <span className="sq-initials">{initials(p.name)}</span>}
        <small>{p.name}</small>
      </div>)}</div>
    </div>)}
  </div>
);

const MatchCard = ({ match, teams }) => {
  const [cardOpen, setCardOpen] = useState(false);
  const [squadOpen, setSquadOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const comp = match.computed;
  const innIdx = match.current_innings || 0;
  const liveInn = comp ? (innIdx === 0 ? comp.inn1 : comp.inn2) : null;
  const hasStats = Boolean(comp && comp.inn1.per_over && comp.inn1.per_over.length > 0);
  const s1 = comp ? shortOf(teams, comp.inn1.team) : "";
  const s2 = comp ? shortOf(teams, comp.inn2.team) : "";
  const wormData = [];
  if (hasStats) {
    comp.inn1.worm.forEach((w) => wormData.push({ over: w.over, [s1]: w.runs }));
    (comp.inn2.worm || []).forEach((w) => {
      const row = wormData.find((r) => r.over === w.over) || wormData[wormData.push({ over: w.over }) - 1];
      row[s2] = w.runs;
    });
  }

  const info = match.teams_info || {};
  return <article className={`score-card ${match.status}`} data-testid={`score-card-${match.id}`}>
    {match.card_mode !== "auto" && match.card_image && <img className="score-card-img" src={`${API}/gallery/file/${match.card_image}`} alt={`${match.team_a} vs ${match.team_b} match card`} data-testid={`score-card-img-${match.id}`} />}
    {(match.card_mode === "auto" || !match.card_image) && (match.teams_info?.a || match.teams_info?.b || true) && <div className="score-card-auto" data-testid={`score-card-auto-${match.id}`}>
      {info.a?.logo_url ? <img src={`${API.replace("/api", "")}${info.a.logo_url}`} alt={match.team_a} /> : <span className="auto-badge" style={{ background: info.a?.colour || "#024ea7" }}>{info.a?.short || match.team_a.slice(0, 3)}</span>}
      <span className="auto-vs">VS</span>
      {info.b?.logo_url ? <img src={`${API.replace("/api", "")}${info.b.logo_url}`} alt={match.team_b} /> : <span className="auto-badge" style={{ background: info.b?.colour || "#f58220" }}>{info.b?.short || match.team_b.slice(0, 3)}</span>}
      {match.sponsor_logo && <img className="auto-sponsor" src={`${API}/gallery/file/${match.sponsor_logo}`} alt="Sponsor" data-testid={`score-card-sponsor-${match.id}`} />}
    </div>}
    <div className="score-top">
      {match.match_no && <span className="score-meta">Match {match.match_no}{match.mtype && match.mtype !== "League" ? ` · ${match.mtype}` : ""}</span>}
      {match.status === "live" && <span className="live-badge" data-testid="live-badge"><i />LIVE</span>}
      {match.status === "completed" && <span className="done-badge">RESULT</span>}
      <span className="score-meta">{match.venue} · {match.date}{match.time ? ` · ${match.time}` : ""}</span>
    </div>
    <div className="score-names">{match.team_a} <em>vs</em> {match.team_b}</div>
    {match.status === "upcoming" && <p className="score-wait">Match yet to begin — stay tuned.</p>}
    {match.status !== "upcoming" && <>
      {hasScore(match.inn1) && <InningsRow teams={teams} inn={match.inn1} fallbackName={match.team_a} highlight={match.status === "live" && innIdx === 0} />}
      {hasScore(match.inn2) && <InningsRow teams={teams} inn={match.inn2} fallbackName={match.team_b} highlight={match.status === "live" && innIdx === 1} />}
      {match.status === "live" && comp?.target != null && innIdx === 1 && <p className="score-need" data-testid="score-need">Need {comp.need} off {comp.balls_left} balls · Target {comp.target} · RRR {comp.rrr}</p>}
      {match.status === "live" && liveInn && (liveInn.striker || liveInn.bowler) && <div className="score-live-detail" data-testid="score-live-detail">
        {liveInn.striker && <p><small>STRIKER</small> {liveInn.striker}{liveInn.striker_stats ? ` — ${liveInn.striker_stats.runs} (${liveInn.striker_stats.balls})` : ""}</p>}
        {liveInn.non_striker && <p><small>NON-STRIKER</small> {liveInn.non_striker}</p>}
        {liveInn.bowler && <p><small>BOWLER</small> {liveInn.bowler}{liveInn.bowler_stats ? ` — ${liveInn.bowler_stats.overs}-${liveInn.bowler_stats.runs}-${liveInn.bowler_stats.wickets}` : ""}</p>}
        {liveInn.partnership && liveInn.partnership.balls > 0 && <p><small>PARTNERSHIP</small> {liveInn.partnership.runs} ({liveInn.partnership.balls})</p>}
        {liveInn.recent?.length > 0 && <div className="ball-chips">{liveInn.recent.map((b, i) => <i key={i} className={b === "W" ? "chip w" : b === "4" || b === "6" ? "chip b" : "chip"}>{b}</i>)}</div>}
      </div>}
      {match.result && <p className="score-result" data-testid="score-result">{match.result}</p>}
      {match.status === "completed" && match.potm && <p className="score-potm" data-testid="score-potm"><Star size={13} fill="currentColor" /> Player of the match: <b>{match.potm}</b></p>}
      {comp && (comp.inn1.batting.length > 0) && <>
        <div className="sc-toggles">
          <button className="sc-toggle" onClick={() => { setCardOpen(!cardOpen); setSquadOpen(false); }} data-testid={`scorecard-toggle-${match.id}`}>{cardOpen ? "Hide full scorecard" : "Full scorecard"}</button>
          {match.squads_public && <button className="sc-toggle" onClick={() => { setSquadOpen(!squadOpen); setCardOpen(false); setStatsOpen(false); }} data-testid={`squads-toggle-${match.id}`}>{squadOpen ? "Hide squads" : "Squads"}</button>}
          {hasStats && <button className="sc-toggle" onClick={() => { setStatsOpen(!statsOpen); setCardOpen(false); setSquadOpen(false); }} data-testid={`stats-toggle-${match.id}`}>{statsOpen ? "Hide stats" : "Stats"}</button>}
        </div>
        {cardOpen && <div className="sc-body" data-testid={`scorecard-${match.id}`}>
          <InningsCard inn={comp.inn1} label="1" />
          {comp.inn2.batting.length > 0 && <InningsCard inn={comp.inn2} label="2" />}
        </div>}
      </>}
    </>}
    {match.status === "upcoming" && match.squads_public && <div className="sc-toggles">
      <button className="sc-toggle" onClick={() => setSquadOpen(!squadOpen)} data-testid={`squads-toggle-${match.id}`}>{squadOpen ? "Hide squads" : "Squads"}</button>
    </div>}
      {squadOpen && match.squads_public && <div className="sq-body" data-testid={`squads-${match.id}`}>
        <SquadTeam side={match.squads_public.a} team={match.team_a} />
        <SquadTeam side={match.squads_public.b} team={match.team_b} />
      </div>}
      {statsOpen && hasStats && <div className="match-stats" data-testid={`match-stats-${match.id}`}>
        <p className="sq-group">Run progression</p>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={wormData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid stroke="rgba(255,255,255,.07)" />
            <XAxis dataKey="over" tick={{ fill: "#8ea5bd", fontSize: 10 }} />
            <YAxis tick={{ fill: "#8ea5bd", fontSize: 10 }} />
            <Tooltip contentStyle={{ background: "#0e2036", border: "1px solid rgba(255,255,255,.15)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
            <Line type="monotone" dataKey={s1} stroke="#f58220" strokeWidth={2.5} dot={false} />
            {comp.inn2.worm?.length > 0 && <Line type="monotone" dataKey={s2} stroke="#00adef" strokeWidth={2.5} dot={false} />}
          </LineChart>
        </ResponsiveContainer>
        <p className="sq-group">Runs per over — {comp.inn1.team}</p>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={comp.inn1.per_over} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid stroke="rgba(255,255,255,.07)" />
            <XAxis dataKey="over" tick={{ fill: "#8ea5bd", fontSize: 10 }} />
            <YAxis tick={{ fill: "#8ea5bd", fontSize: 10 }} />
            <Tooltip contentStyle={{ background: "#0e2036", border: "1px solid rgba(255,255,255,.15)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
            <Bar dataKey="runs" fill="#e6232d" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        {comp.inn2.per_over?.length > 0 && <>
          <p className="sq-group">Runs per over — {comp.inn2.team}</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={comp.inn2.per_over} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
              <CartesianGrid stroke="rgba(255,255,255,.07)" />
              <XAxis dataKey="over" tick={{ fill: "#8ea5bd", fontSize: 10 }} />
              <YAxis tick={{ fill: "#8ea5bd", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#0e2036", border: "1px solid rgba(255,255,255,.15)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
              <Bar dataKey="runs" fill="#00adef" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>}
      </div>}
    {match.commentary && match.commentary.length > 0 && <div className="comm-feed" data-testid="comm-feed">
      <p className="comm-head"><MessageSquareText size={13} /> COMMENTARY</p>
      {match.commentary.slice(0, 20).map((entry, index) => <p className="comm-line" key={index} data-testid={`comm-line-${index}`}>{entry.text}</p>)}
    </div>}
  </article>;
};

export default function LiveScores() {
  const [matches, setMatches] = useState(null);
  const [teams, setTeams] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [socketLive, setSocketLive] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/matches`).then((r) => { setMatches(r.data.matches || []); setUpdatedAt(new Date()); }).catch(() => setMatches([]));
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 15000);
    axios.get(`${API}/kpl`).then((r) => setTeams(r.data.teams || [])).catch(() => {});
    let retry;
    const connect = () => {
      try {
        const ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/matches`);
        wsRef.current = ws;
        ws.onopen = () => setSocketLive(true);
        ws.onmessage = () => load();
        ws.onclose = () => { setSocketLive(false); retry = setTimeout(connect, 5000); };
        ws.onerror = () => ws.close();
      } catch { retry = setTimeout(connect, 5000); }
    };
    connect();
    return () => { clearInterval(poll); clearTimeout(retry); if (wsRef.current) wsRef.current.onclose = null, wsRef.current.close(); };
  }, [load]);

  const live = (matches || []).filter((m) => m.status === "live");
  const upcoming = (matches || []).filter((m) => m.status === "upcoming");
  const completed = (matches || []).filter((m) => m.status === "completed");

  return <div className="gallery-wrap live-wrap">
    <div className="container">
      <div className="gallery-top">
        <img className="auth-logo gallery-logo" src={LOGO_WHITE_URL} alt="KPL logo" data-testid="live-logo" />
        <Link className="auth-home gallery-back" to="/" data-testid="live-back-home"><ArrowLeft size={15} /> BACK TO SITE</Link>
      </div>
      <div className="gallery-head">
        <p className="eyebrow blue">KPL SEASON ONE</p>
        <h1 className="gallery-title">Live scores.</h1>
        <p className="live-note" data-testid="live-note"><Radio size={14} /> {socketLive ? "Connected live — updates arrive instantly" : "Scores refresh automatically every 15 seconds"}{updatedAt ? ` · updated ${updatedAt.toLocaleTimeString("en-IN")}` : ""}.</p>
      </div>

      {matches === null ? <p className="reg-empty">Loading scores…</p> : matches.length === 0 ? <div className="gallery-empty" data-testid="live-empty">
        <Radio size={40} />
        <h3>No matches yet</h3>
        <p>Fixtures are announced after the player auction on 25 November 2026. Once the season begins, live scores and ball-by-ball commentary appear here.</p>
      </div> : <>
        {live.length > 0 && <section data-testid="live-section"><h2 className="score-section-title"><span className="live-badge"><i />LIVE NOW</span></h2>{live.map((m) => <MatchCard key={m.id} match={m} teams={teams} />)}</section>}
        {upcoming.length > 0 && <section data-testid="upcoming-section"><h2 className="score-section-title">UPCOMING</h2>{upcoming.map((m) => <MatchCard key={m.id} match={m} teams={teams} />)}</section>}
        {completed.length > 0 && <section data-testid="completed-section"><h2 className="score-section-title">RESULTS</h2>{completed.map((m) => <MatchCard key={m.id} match={m} teams={teams} />)}</section>}
      </>}
    </div>
  </div>;
}
